import React, {useCallback, useMemo, useState} from 'react'
import styled, {CSSProperties} from 'styled-components'
import convertUnit from 'lib/unit'
import {ThemeColor} from 'themes'
import {Icon} from '../Icon'
import {RadioItemProps} from './RadioItemProps'
import {Paragraph} from '../Paragraph'

interface StyledIconContainerProps extends RadioItemProps {
  size: number
  hover: boolean
}

interface StyledDescriptionContainerProps {
  cursor: CSSProperties['cursor']
}

interface StyledParagraphProps {
  hoverColor?: ThemeColor
}

const StyledParagraph = styled(Paragraph)<StyledParagraphProps>`
  ${({theme, hoverColor}) => ({
    ':hover': {
      color: hoverColor && theme[hoverColor],
    },
  })}
  transition: color 0.15s ease-out;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  box-sizing: border-box;
  align-items: center;
`

const StyledIconContainer = styled.div<StyledIconContainerProps>`
  ${({isSelected, isDisabled, size, theme, hover}) => ({
    backgroundColor: isSelected ? theme.primary_5 : theme.white_1,
    width: convertUnit(size),
    height: convertUnit(size),
    cursor: isDisabled ? 'default' : 'pointer',
    ...(!isSelected && {
      borderStyle: 'solid',
      borderWidth: convertUnit(1),
      borderColor: hover ? theme.gray_5 : theme.gray_3,
    }),
  })}
  display: flex;
  box-sizing: border-box;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease-out;
`

const StyledDescriptionContainer = styled.div<StyledDescriptionContainerProps>`
  flex: 1;
  padding: 0 ${convertUnit(8)};
  user-select: none;
  cursor: ${({cursor}) => cursor};
`

export default function RadioItem({
  isSelected,
  isDisabled,
  size = 24,
  containerStyle,
  description,
  fontSize,
  fontWeight,
  color,
  onClick,
  onMouseEnter,
  onMouseLeave,
  ...props
}: RadioItemProps) {
  const [hover, setHover] = useState(false)

  const handleMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      onMouseEnter && onMouseEnter(event)
      setHover(true)
    },
    [onMouseEnter],
  )

  const handleMouseLeave = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      onMouseLeave && onMouseLeave(event)
      setHover(false)
    },
    [onMouseLeave],
  )

  const handleRenderIcon = useMemo(
    () =>
      !isDisabled &&
      isSelected && <Icon size={(size * 2) / 3} type="check" color="white_1" />,
    [isDisabled, isSelected, size],
  )

  const handleRenderDescription = useMemo(
    () =>
      description ? (
        <StyledDescriptionContainer cursor={isDisabled ? 'default' : 'pointer'}>
          {typeof description === 'string' ? (
            <StyledParagraph
              color={isDisabled ? 'gray_2' : color || 'gray_5'}
              hoverColor={isDisabled ? undefined : 'black_2'}
              fontWeight={fontWeight || 'regular'}
              fontSize={fontSize || 's'}>
              {description}
            </StyledParagraph>
          ) : (
            description
          )}
        </StyledDescriptionContainer>
      ) : null,
    [description, isDisabled, color, fontWeight, fontSize],
  )

  return (
    <StyledContainer
      data-testid="radioItem"
      style={containerStyle}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <StyledIconContainer
        {...props}
        size={size}
        isSelected={isSelected}
        isDisabled={isDisabled}
        hover={hover}>
        {handleRenderIcon}
      </StyledIconContainer>
      {handleRenderDescription}
    </StyledContainer>
  )
}
